import React, { useEffect, useRef } from 'react';
import { useSensory } from '../context/SensoryContext';

const LINK_DIST = 140;
const POINTER_DIST = 180;
const MAX_SPARKS = 6;

const readColors = () => {
  const s = getComputedStyle(document.documentElement);
  return {
    wire: s.getPropertyValue('--wire').trim(),
    node: s.getPropertyValue('--node').trim(),
    accent: s.getPropertyValue('--accent').trim(),
  };
};

// A drifting network of nodes. Nearby nodes wire together, the cursor
// energises the network, and sparks travel along random connections.
// With reduced motion it draws a single still frame.
const WiringCanvas = () => {
  const canvasRef = useRef(null);
  const { settings, reducedMotion } = useSensory();

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    // Effect re-runs on theme/calm change, so colours are always current.
    const colors = readColors();
    let width = 0;
    let height = 0;
    let nodes = [];
    let sparks = [];
    let frame = null;
    let visible = true;
    const pointer = { x: -9999, y: -9999 };

    const seed = () => {
      const count = Math.min(110, Math.floor((width * height) / 11000));
      nodes = Array.from({ length: count }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.35,
        vy: (Math.random() - 0.5) * 0.35,
        r: Math.random() * 1.6 + 0.8,
      }));
      sparks = [];
    };

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = rect.width;
      height = rect.height;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      seed();
      if (reducedMotion) draw();
    };

    const spawnSpark = () => {
      if (sparks.length >= MAX_SPARKS || nodes.length < 2) return;
      const a = nodes[Math.floor(Math.random() * nodes.length)];
      let best = null;
      let bestD = LINK_DIST;
      for (const b of nodes) {
        if (b === a) continue;
        const d = Math.hypot(a.x - b.x, a.y - b.y);
        if (d < bestD) {
          best = b;
          bestD = d;
        }
      }
      if (best) sparks.push({ a, b: best, t: 0, speed: 0.012 + Math.random() * 0.02 });
    };

    const draw = () => {
      ctx.clearRect(0, 0, width, height);

      // Wires
      for (let i = 0; i < nodes.length; i++) {
        const a = nodes[i];
        for (let j = i + 1; j < nodes.length; j++) {
          const b = nodes[j];
          const d = Math.hypot(a.x - b.x, a.y - b.y);
          if (d > LINK_DIST) continue;
          const mid = Math.hypot((a.x + b.x) / 2 - pointer.x, (a.y + b.y) / 2 - pointer.y);
          const energised = mid < POINTER_DIST;
          ctx.globalAlpha = (1 - d / LINK_DIST) * (energised ? 0.9 : 1);
          ctx.strokeStyle = energised ? colors.accent : colors.wire;
          ctx.lineWidth = energised ? 1.2 : 1;
          ctx.beginPath();
          ctx.moveTo(a.x, a.y);
          ctx.lineTo(b.x, b.y);
          ctx.stroke();
        }
      }

      // Nodes
      for (const n of nodes) {
        const near = Math.hypot(n.x - pointer.x, n.y - pointer.y) < POINTER_DIST;
        ctx.globalAlpha = 1;
        ctx.fillStyle = near ? colors.accent : colors.node;
        ctx.beginPath();
        ctx.arc(n.x, n.y, near ? n.r + 1 : n.r, 0, Math.PI * 2);
        ctx.fill();
      }

      // Sparks
      for (const s of sparks) {
        const x = s.a.x + (s.b.x - s.a.x) * s.t;
        const y = s.a.y + (s.b.y - s.a.y) * s.t;
        const glow = ctx.createRadialGradient(x, y, 0, x, y, 10);
        glow.addColorStop(0, colors.accent);
        glow.addColorStop(1, 'transparent');
        ctx.globalAlpha = Math.sin(Math.PI * s.t);
        ctx.fillStyle = glow;
        ctx.beginPath();
        ctx.arc(x, y, 10, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
    };

    const step = () => {
      for (const n of nodes) {
        // Gentle pull towards the cursor
        const dx = pointer.x - n.x;
        const dy = pointer.y - n.y;
        const d = Math.hypot(dx, dy);
        if (d < POINTER_DIST && d > 1) {
          n.vx += (dx / d) * 0.012;
          n.vy += (dy / d) * 0.012;
        }
        n.vx *= 0.995;
        n.vy *= 0.995;
        const speed = Math.hypot(n.vx, n.vy);
        if (speed > 0.9) {
          n.vx = (n.vx / speed) * 0.9;
          n.vy = (n.vy / speed) * 0.9;
        }
        n.x += n.vx;
        n.y += n.vy;
        if (n.x < 0 || n.x > width) n.vx *= -1;
        if (n.y < 0 || n.y > height) n.vy *= -1;
      }
      if (Math.random() < 0.06) spawnSpark();
      sparks = sparks.filter((s) => (s.t += s.speed) < 1);
    };

    const loop = () => {
      if (visible && !document.hidden) {
        step();
        draw();
      }
      frame = requestAnimationFrame(loop);
    };

    const onMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      pointer.x = e.clientX - rect.left;
      pointer.y = e.clientY - rect.top;
    };
    const onLeave = () => {
      pointer.x = -9999;
      pointer.y = -9999;
    };

    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);
    const io = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
    });
    io.observe(canvas);

    const host = canvas.parentElement;
    if (reducedMotion) {
      draw();
    } else {
      host.addEventListener('pointermove', onMove);
      host.addEventListener('pointerleave', onLeave);
      frame = requestAnimationFrame(loop);
    }

    return () => {
      cancelAnimationFrame(frame);
      ro.disconnect();
      io.disconnect();
      host.removeEventListener('pointermove', onMove);
      host.removeEventListener('pointerleave', onLeave);
    };
  }, [reducedMotion, settings.theme, settings.calm]);

  return <canvas ref={canvasRef} className="wiring-canvas" aria-hidden="true" />;
};

export default WiringCanvas;
